import { connectRouter, routerMiddleware, RouterState } from 'connected-react-router';
import { History } from 'history';
import {
	Action as ReduxAction,
	applyMiddleware,
	combineReducers,
	compose,
	createStore as createReduxStore,
	Store as ReduxStore,
} from 'redux';
import createServiceThunk, {
	ThunkAction as ReduxThunkAction,
	ThunkDispatch as ReduxThunkDispatch,
} from 'redux-thunk-service';
import { reducer as accessToken, State as AccessTokenState } from '~/AccessToken';
import type UserProductGroupApi from '~/Api/User/ProductGroup/UserProductGroupApi';
import ApiClient, { reducer as apiClient, State as ApiClientState } from '~/ApiClient';
import AuthenticatedApiClient from '~/ApiClient/AuthenticatedApiClient';
import { reducer as applicationUpdate, State as ApplicationUpdateState } from '~/ApplicationUpdate';
import { reducer as backlink, State as BacklinkState } from '~/Backlink';
import { reducer as bgMaxPrice, State as BgMaxPriceState } from '~/BgMaxPrice';
import CopyGroupProductManager from '~/CopyGroupProduct/CopyGroupProductManager';
import { reducer as country, State as CountryState } from '~/Country';
import { reducer as currency, State as CurrencyState } from '~/Currency';
import {
	reducer as customerDatasourceLicences,
	State as CustomerDatasourceLicencesState,
} from '~/CustomerDatasourceLicence';
import { reducer as czMaxPrice, State as CzMaxPriceState } from '~/CzMaxPrice';
import { reducer as czReimbursement, State as CzReimbursementState } from '~/CzReimbursement';
import { reducer as datasourceComputedPrice, State as DatasourceComputedPriceState } from '~/DatasourceComputedPrice';
import { reducer as dataSourceStatus, State as DataSourceStatusState } from '~/DataSourceStatus';
import { reducer as erpScenarioComparisonState, State as ERPScenarioComparisonState } from '~/ERP';
import type ErrorHandlerManager from '~/ErrorHandler/ErrorHandlerManager';
import { reducer as exchangeRate, State as ExchangeRateState } from '~/ExchangeRate';
import { reducer as flashMessages, State as FlashMessagesState } from '~/FlashMessage';
import { reducer as groupProduct, State as GroupProductState } from '~/GroupProduct';
import type { IGroupProductManager } from '~/GroupProduct/GroupProductManager';
import { reducer as groupProductHistory, State as GroupProductHistory } from '~/GroupProductHistory';
import { reducer as hrMaxPrice, State as HrMaxPriceState } from '~/HrMaxPrice';
import { reduxLoggingMiddleware } from '~/Logger';
import { reducer as ltMaxPrice, State as LtMaxPriceState } from '~/LtMaxPrice';
import {
	reducer as medicinalBrandsAnalyticsAtcAutocomplete,
	State as MedicinalBrandsAnalyticsAtcAutocompleteState,
} from '~/MedicinalBrandsAnalytics/AtcAutocomplete';
import {
	reducer as medicinalBrandsAnalyticsBrandAutocomplete,
	State as MedicinalBrandsAnalyticsBrandAutocompleteState,
} from '~/MedicinalBrandsAnalytics/BrandAutocomplete';
import {
	reducer as medicinalBrandsAnalyticsDataSource,
	State as MedicinalBrandsAnalyticsDataSourceState,
} from '~/MedicinalBrandsAnalytics/DataSource';
import {
	reducer as medicinalBrandsAnalyticsRegion,
	State as MedicinalBrandsAnalyticsRegionState,
} from '~/MedicinalBrandsAnalytics/Region';
import {
	reducer as medicinalBrandsAnalyticsTherapeuticAreaAutocomplete,
	State as MedicinalBrandsAnalyticsTherapeuticAreaAutocompleteState,
} from '~/MedicinalBrandsAnalytics/TherapeuticAreaAutocomplete';
import {
	reducer as medicinalBrandsAnalyticsVirtualBrand,
	State as MedicinalBrandsAnalyticsVirtualBrandState,
} from '~/MedicinalBrandsAnalytics/VirtualBrand';
import {
	reducer as medicinalBrandsAnalyticsHistory,
	State as MedicinalBrandsAnalyticsHistoryState,
} from '~/MedicinalBrandsAnalytics/VirtualBrand/History';
import {
	reducer as medicinalBrandsAnalyticsRegistration,
	State as MedicinalBrandsAnalyticsRegistrationState,
} from '~/MedicinalBrandsAnalytics/VirtualBrand/Registration';
import {
	reducer as medicinalBrandsAnalyticsReimbursement,
	State as MedicinalBrandsAnalyticsReimbursementState,
} from '~/MedicinalBrandsAnalytics/VirtualBrand/Reimbursement';
import {
	reducer as medicinalBrandsAnalyticsTimeToPrice,
	State as MedicinalBrandsAnalyticsTimeToPriceState,
} from '~/MedicinalBrandsAnalytics/VirtualBrand/TimeToPrice';
import { reducer as passwordChange, State as PasswordChangeState } from '~/PasswordChange';
import { reducer as passwordReset, State as PasswordResetState } from '~/PasswordReset';
import { IPersistentStateManager } from '~/PersistentState/PersistentStateManager';
import { reducer as priceReferencing, State as PriceReferencingState } from '~/PriceReferencing';
import { reducer as products, State as ProductsState } from '~/Product';
import { reducer as productDataset, State as ProductDatasetState } from '~/ProductDataset';
import { reducer as productGroup, State as ProductGroupState } from '~/ProductGroup';
import { reducer as productHistory, State as ProductHistoryState } from '~/ProductHistory';
import { reducer as productPrice, State as ProductPriceState } from '~/ProductPrice';
import { reducer as productPriceHistory, State as ProductPriceHistoryState } from '~/ProductPriceHistory';
import {
	reducer as productSearchableDataSource,
	State as ProductSearchableDataSourceState,
} from '~/ProductSearchableDataSource';
import { reducer as roMaxPrice, State as RoMaxPriceState } from '~/RoMaxPrice';
import { reducer as siMaxPrice, State as SiMaxPriceState } from '~/SiMaxPrice';
import { reducer as skMaxPrice, State as SkMaxPriceState } from '~/SkMaxPrice';
import treeBrowser, { State as TreeBrowserState } from '~/TreeBrowser/reducer';

export interface State {
	accessToken: AccessTokenState;
	apiClient: ApiClientState;
	applicationUpdate: ApplicationUpdateState;
	backlink: BacklinkState;
	country: CountryState;
	customerDatasourceLicences: CustomerDatasourceLicencesState;
	czMaxPrice: CzMaxPriceState;
	czReimbursement: CzReimbursementState;
	flashMessages: FlashMessagesState;
	passwordChange: PasswordChangeState;
	passwordReset: PasswordResetState;
	productHistory: ProductHistoryState;
	productPriceHistory: ProductPriceHistoryState;
	productPrice: ProductPriceState;
	products: ProductsState;
	router: RouterState;
	groupProduct: GroupProductState;
	groupProductHistory: GroupProductHistory;
	treeBrowser: TreeBrowserState;
	skMaxPrice: SkMaxPriceState;
	datasourceComputedPrice: DatasourceComputedPriceState;
	exchangeRate: ExchangeRateState;
	currency: CurrencyState;
	priceReferencing: PriceReferencingState;
	productSearchableDataSource: ProductSearchableDataSourceState;
	dataSourceStatus: DataSourceStatusState;
	productGroup: ProductGroupState;
	productDataset: ProductDatasetState;
	siMaxPrice: SiMaxPriceState;
	hrMaxPrice: HrMaxPriceState;
	bgMaxPrice: BgMaxPriceState;
	roMaxPrice: RoMaxPriceState;
	ltMaxPrice: LtMaxPriceState;
	medicinalBrandsAnalyticsAtcAutocomplete: MedicinalBrandsAnalyticsAtcAutocompleteState;
	medicinalBrandsAnalyticsBrandAutocomplete: MedicinalBrandsAnalyticsBrandAutocompleteState;
	medicinalBrandsAnalyticsTherapeuticAreaAutocomplete: MedicinalBrandsAnalyticsTherapeuticAreaAutocompleteState;
	medicinalBrandsAnalyticsVirtualBrand: MedicinalBrandsAnalyticsVirtualBrandState;
	medicinalBrandsAnalyticsRegion: MedicinalBrandsAnalyticsRegionState;
	medicinalBrandsAnalyticsDataSource: MedicinalBrandsAnalyticsDataSourceState;
	medicinalBrandsAnalyticsRegistration: MedicinalBrandsAnalyticsRegistrationState;
	medicinalBrandsAnalyticsTimeToPrice: MedicinalBrandsAnalyticsTimeToPriceState;
	medicinalBrandsAnalyticsReimbursement: MedicinalBrandsAnalyticsReimbursementState;
	medicinalBrandsAnalyticsHistory: MedicinalBrandsAnalyticsHistoryState;
	erpScenarioComparisonState: ERPScenarioComparisonState;
}

export type ApplicationState = State;

export type Services = {
	apiClient: ApiClient;
	authenticatedApiClient: AuthenticatedApiClient;
	copyGroupProductManager: CopyGroupProductManager;
	errorHandlerManager: ErrorHandlerManager;
	history: History;
	groupProductManager: IGroupProductManager;
	persistentStateManager: IPersistentStateManager;
	userProductGroupApi: UserProductGroupApi;
	window: typeof window;
};
export type ThunkAction<Result, Action extends ReduxAction> = ReduxThunkAction<Result, State, Services, Action>;
export type ThunkDispatch<Action extends ReduxAction> = ReduxThunkDispatch<State, Services, Action>;
export type Store = ReduxStore<State, any>;

export type DispatchGetState = { dispatch: Store['dispatch']; getState: Store['getState'] };

export interface PreloadedState {
	apiClient: ApiClientState;
	applicationUpdate: ApplicationUpdateState;
	erpScenarioComparisonState: ERPScenarioComparisonState;
}

function createReducer(history: History) {
	return combineReducers({
		router: connectRouter(history),
		apiClient,
		applicationUpdate,
		accessToken,
		passwordChange,
		passwordReset,
		country,
		flashMessages,
		products,
		productPrice,
		productHistory,
		productPriceHistory,
		groupProduct,
		groupProductHistory,
		czMaxPrice,
		czReimbursement,
		skMaxPrice,
		customerDatasourceLicences,
		backlink,
		treeBrowser,
		datasourceComputedPrice,
		exchangeRate,
		currency,
		priceReferencing,
		productSearchableDataSource,
		dataSourceStatus,
		productGroup,
		productDataset,
		siMaxPrice,
		hrMaxPrice,
		bgMaxPrice,
		roMaxPrice,
		ltMaxPrice,
		medicinalBrandsAnalyticsAtcAutocomplete,
		medicinalBrandsAnalyticsBrandAutocomplete,
		medicinalBrandsAnalyticsTherapeuticAreaAutocomplete,
		medicinalBrandsAnalyticsVirtualBrand,
		medicinalBrandsAnalyticsRegion,
		medicinalBrandsAnalyticsDataSource,
		medicinalBrandsAnalyticsRegistration,
		medicinalBrandsAnalyticsTimeToPrice,
		medicinalBrandsAnalyticsReimbursement,
		medicinalBrandsAnalyticsHistory,
		erpScenarioComparisonState,
	});
}

export function createStore(
	preloadedState: PreloadedState,
	history: History,
	serviceFactory: (dispatch: Store['dispatch'], getState: Store['getState']) => Services,
	customCompose?: typeof compose
): Store {
	const composeEnhancers = customCompose ?? compose;

	return createReduxStore(
		createReducer(history),
		preloadedState,
		composeEnhancers(
			applyMiddleware(
				createServiceThunk<
					ApplicationState,
					ReduxAction<any>,
					(dispatch: Store['dispatch'], getState: Store['getState']) => Services
				>(serviceFactory)
			),
			applyMiddleware(routerMiddleware(history)),
			applyMiddleware(reduxLoggingMiddleware)
		)
	);
}
